import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DefaultUrlSerializer,
  NavigationEnd,
  Router,
  RouterLink,
  convertToParamMap,
  type Params,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { IconComponent } from '@shared/icons/icon/icon.component';
import {
  collectionNavigationQuery,
  collectionPath,
} from '@features/console/pages/monitoring/shared/monitoring-navigation';
import { ShellStateService } from '../shell/shell-state.service';
import { ThemeToggleComponent } from './theme-toggle/theme-toggle.component';

interface Crumb {
  labelKey: string;
  labelParams?: Record<string, string>;
  link?: string;
  queryParams?: Params;
}

const PLATFORM_PAGES: Record<string, string> = {
  tenants: 'SIDEBAR.NAV.TENANTS',
  accounts: 'SIDEBAR.NAV.ACCOUNTS_MANAGEMENT',
  groups: 'SIDEBAR.NAV.GROUPS',
};

const CONSOLE_PAGES: Record<string, string> = {
  dashboard: 'NAV.DASHBOARD',
  agents: 'NAV.AGENTS',
  'api-keys': 'NAV.API_KEYS',
  monitoring: 'NAV.MONITORING',
  'audit-logs': 'NAV.AUDIT_LOGS',
};

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

/** Breadcrumb trail for a URL, e.g. `/groups/42` → Platform / Groups (+ detail label). */
export function breadcrumbFor(url: string): Crumb[] {
  const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
  const [first] = segments;
  if (first && PLATFORM_PAGES[first]) {
    const page: Crumb = { labelKey: PLATFORM_PAGES[first] };
    if (segments.length > 1) page.link = `/${first}`;
    return [{ labelKey: 'SIDEBAR.SECTION_PLATFORM' }, page];
  }
  if (first === 'console') {
    const monitoringIndex =
      segments[2] === 'monitoring' ? 2 : segments[3] === 'monitoring' ? 3 : -1;
    if (monitoringIndex !== -1) {
      const view = segments[monitoringIndex + 1];
      const section: Crumb = { labelKey: 'SIDEBAR.SECTION_AI' };
      if (view === 'analytics') {
        return [section, { labelKey: 'MONITORING.TABS.ANALYTICS' }];
      }
      if (view === 'errors') {
        return [section, { labelKey: 'MONITORING.ERRORS.NAV_TITLE' }];
      }
      const monitoring: Crumb = { labelKey: 'NAV.MONITORING' };
      const entity = segments[monitoringIndex + 2];
      if (entity && (view === 'requests' || view === 'conversations')) {
        const query = new DefaultUrlSerializer().parse(url).queryParams;
        const params = convertToParamMap(query);
        const destination = collectionPath(params);
        monitoring.labelKey =
          destination === 'errors' ? 'MONITORING.ERRORS.NAV_TITLE' : 'NAV.MONITORING';
        monitoring.link = `/${segments.slice(0, monitoringIndex + 1).join('/')}/${destination}`;
        monitoring.queryParams = {
          ...query,
          ...collectionNavigationQuery(params),
        };
        return [
          section,
          monitoring,
          view === 'requests'
            ? {
                labelKey: 'MONITORING.REQUEST_DETAIL.NAV.PROMPT',
                labelParams: { id: decodeURIComponent(entity) },
              }
            : { labelKey: 'MONITORING.CONVERSATION.SUMMARY.EYEBROW' },
        ];
      }
      return [section, monitoring];
    }
    const page = segments.find((s) => CONSOLE_PAGES[s]);
    return [
      { labelKey: 'SIDEBAR.SECTION_AI' },
      ...(page ? [{ labelKey: CONSOLE_PAGES[page] }] : []),
    ];
  }
  if (first === 'help') return [{ labelKey: 'SIDEBAR.NAV.HELP' }];
  return [];
}

@Component({
  selector: 'mtm-app-header',
  standalone: true,
  imports: [RouterLink, TranslatePipe, IconComponent, ThemeToggleComponent, NgTemplateOutlet],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeaderComponent {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  readonly shell = inject(ShellStateService);

  readonly languages = LANGUAGES;

  private readonly _currentUrl = signal(this.router.url);

  readonly currentLang = signal(this.translate.currentLang ?? 'en');

  readonly crumbs = computed(() => breadcrumbFor(this._currentUrl()));

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        this._currentUrl.set(e.urlAfterRedirects);
        this.shell.mobileNavOpen.set(false);
      });
  }

  switchLang(code: string): void {
    this.translate.use(code);
    this.currentLang.set(code);
  }
}
